import { trackDownload } from '../../utils/downloadIndicator';
import React, { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Divider,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useTheme,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PublicPageShell from '../../components/PublicPageShell';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addStruxorPdfFooter, reservePdfFooterSpace } from '../../utils/pdfBranding';

const PRIMARY = '#0087c1';

const UNITS = ['each', 'm²', 'm', 'hr', 'day', 'lot'];

const PAYMENT_TERMS_OPTIONS = [
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: 'completion', label: 'On completion' },
  { value: 'custom', label: 'Custom' },
];

const genInvoiceNumber = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `INV-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${Math.floor(Math.random() * 900 + 100)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

let nextLineId = 2;

const emptyLine = (id) => ({ id, description: '', qty: '1', unit: 'each', unitPrice: '', total: 0 });

const fmt = (n) =>
  Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoiceTemplate() {
  const theme = useTheme();
  
  
  // Company details
  const [company, setCompany] = useState({ name: '', address: '', phone: '', email: '', vatNumber: '' });

  // Client details
  const [client, setClient] = useState({ name: '', address: '', contact: '', email: '' });

  // Invoice meta
  const [invoiceMeta, setInvoiceMeta] = useState({
    number: genInvoiceNumber(),
    date: todayISO(),
    dueDate: addDays(30),
    paymentTerms: '30',
    customTerms: '',
  });

  // Project / site reference
  const [projectRef, setProjectRef] = useState('');

  // Quote / PO reference
  const [poRef, setPoRef] = useState('');

  // Line items
  const [lines, setLines] = useState([emptyLine(1)]);

  // CIS
  const [cisEnabled, setCisEnabled] = useState(false);
  const [cisRate, setCisRate] = useState('20');

  // VAT
  const [vatRate, setVatRate] = useState('20');

  // Bank details
  const [bank, setBank] = useState({ bankName: '', accountName: '', sortCode: '', accountNumber: '' });

  // Notes
  const [notes, setNotes] = useState('');

  // ───── derived totals ─────
  const computedLines = lines.map((l) => {
    const qty = parseFloat(l.qty) || 0;
    const price = parseFloat(l.unitPrice) || 0;
    return { ...l, total: qty * price };
  });
  const subtotal = computedLines.reduce((s, l) => s + l.total, 0);
  const cisPct = cisEnabled ? (parseFloat(cisRate) || 0) : 0;
  const cisAmount = subtotal * (cisPct / 100);
  const vatPct = parseFloat(vatRate) || 0;
  const vatableAmount = subtotal - cisAmount;
  const vatAmount = vatableAmount * (vatPct / 100);
  const grandTotal = vatableAmount + vatAmount;

  // ───── line-item helpers ─────
  const addLine = () => setLines((prev) => [...prev, emptyLine(nextLineId++)]);
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

  const updateLine = (id, field, value) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  // ───── payment terms change ─────
  const handlePaymentTermsChange = (value) => {
    let dueDate = invoiceMeta.dueDate;
    if (value === '14') dueDate = addDays(14);
    else if (value === '30') dueDate = addDays(30);
    else if (value === '60') dueDate = addDays(60);
    setInvoiceMeta((prev) => ({ ...prev, paymentTerms: value, dueDate }));
  };

  // ───── PDF generation ─────
  const downloadPDF = useCallback(async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header — company name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name || 'Your Company Name', 14, y);
    y += 8;

    // Company address block
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (company.address) { doc.text(company.address, 14, y); y += 4; }
    if (company.phone) { doc.text(`Tel: ${company.phone}`, 14, y); y += 4; }
    if (company.email) { doc.text(`Email: ${company.email}`, 14, y); y += 4; }
    if (company.vatNumber) { doc.text(`VAT/Reg: ${company.vatNumber}`, 14, y); y += 4; }

    // Invoice title — right side
    y = Math.max(y, 36);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - 14, 20, { align: 'right' });

    // Invoice meta — right side
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metaX = pageWidth - 14;
    doc.text(`Invoice #: ${invoiceMeta.number}`, metaX, 28, { align: 'right' });
    doc.text(`Date: ${invoiceMeta.date}`, metaX, 33, { align: 'right' });
    doc.text(`Due Date: ${invoiceMeta.dueDate}`, metaX, 38, { align: 'right' });
    const termsLabel = invoiceMeta.paymentTerms === 'custom'
      ? (invoiceMeta.customTerms || 'Custom')
      : (PAYMENT_TERMS_OPTIONS.find((o) => o.value === invoiceMeta.paymentTerms)?.label || '');
    doc.text(`Payment Terms: ${termsLabel}`, metaX, 43, { align: 'right' });

    // Divider
    y += 2;
    doc.setDrawColor(200);
    doc.line(14, y, pageWidth - 14, y);
    y += 6;

    // Client details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (client.name) { doc.text(client.name, 14, y); y += 4; }
    if (client.contact) { doc.text(`Attn: ${client.contact}`, 14, y); y += 4; }
    if (client.address) { doc.text(client.address, 14, y); y += 4; }
    if (client.email) { doc.text(`Email: ${client.email}`, 14, y); y += 4; }

    // References
    if (projectRef.trim() || poRef.trim()) {
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      if (projectRef.trim()) {
        doc.text('Project/Site Ref:', 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(projectRef, 55, y);
        y += 5;
        doc.setFont('helvetica', 'bold');
      }
      if (poRef.trim()) {
        doc.text('Quote/PO Ref:', 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(poRef, 55, y);
        y += 5;
      }
    }

    // Line items table
    y += 4;
    const tableBody = computedLines.map((l) => [
      l.description,
      l.qty,
      l.unit,
      `£${fmt(parseFloat(l.unitPrice) || 0)}`,
      `£${fmt(l.total)}`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: tableBody,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 135, 193], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 20 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 28 },
        4: { halign: 'right', cellWidth: 28 },
      },
      margin: { left: 14, right: 14, bottom: 24 },
    });

    y = doc.lastAutoTable.finalY + 6;

    // Totals
    y = reservePdfFooterSpace(doc, y, cisEnabled ? 24 : 18);
    const totalsX = pageWidth - 14;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', totalsX - 40, y);
    doc.text(`£${fmt(subtotal)}`, totalsX, y, { align: 'right' });
    y += 5;

    if (cisEnabled) {
      doc.text(`CIS Deduction (${cisPct}%):`, totalsX - 40, y);
      doc.text(`-£${fmt(cisAmount)}`, totalsX, y, { align: 'right' });
      y += 5;
    }

    doc.text(`VAT (${vatPct}%):`, totalsX - 40, y);
    doc.text(`£${fmt(vatAmount)}`, totalsX, y, { align: 'right' });
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Due:', totalsX - 40, y);
    doc.text(`£${fmt(grandTotal)}`, totalsX, y, { align: 'right' });

    // Bank details
    if (bank.bankName || bank.accountName || bank.sortCode || bank.accountNumber) {
      const bankLineCount = [bank.bankName, bank.accountName, bank.sortCode, bank.accountNumber].filter(Boolean).length;
      y = reservePdfFooterSpace(doc, y + 10, bankLineCount * 4 + 8);
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Bank Details', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      if (bank.bankName) { doc.text(`Bank: ${bank.bankName}`, 14, y); y += 4; }
      if (bank.accountName) { doc.text(`Account Name: ${bank.accountName}`, 14, y); y += 4; }
      if (bank.sortCode) { doc.text(`Sort Code: ${bank.sortCode}`, 14, y); y += 4; }
      if (bank.accountNumber) { doc.text(`Account Number: ${bank.accountNumber}`, 14, y); y += 4; }
    }

    // Notes
    if (notes.trim()) {
      const noteLines = doc.splitTextToSize(notes, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 4, noteLines.length * 3.5 + 8);
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Instructions / Notes', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(noteLines, 14, y);
    }

    await addStruxorPdfFooter(doc);
    doc.save(`${invoiceMeta.number}.pdf`);
  }, [company, client, invoiceMeta, projectRef, poRef, computedLines, subtotal, cisEnabled, cisPct, cisAmount, vatPct, vatAmount, grandTotal, bank, notes]);

  // ───── section header helper ─────
  const SectionTitle = ({ children }) => (
    <Typography variant="h6" fontWeight={700} sx={{ color: PRIMARY, mb: 2 }}>
      {children}
    </Typography>
  );

  // ───── render ─────
  return (
    <>
      <Helmet>
        <title>Free Construction Invoice Template — Create &amp; Download PDF | Struxor</title>
        <meta
          name="description"
          content="Free construction invoice template with CIS support. Fill in your company and client details, add line items, calculate VAT and CIS deductions, and download a professional invoice as PDF. No sign-up required."
        />
        <link rel="canonical" href="https://www.struxorapp.com/tools/invoice-template" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Construction Invoice Template',
          description: 'Free online construction invoice template. Create professional invoices with CIS deductions, VAT calculations, and download as PDF.',
          url: 'https://www.struxorapp.com/tools/invoice-template',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
          author: { '@type': 'Organization', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      <PublicPageShell>

      {/* ── Hero ── */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: 6, px: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" mb={2}>
          <ReceiptLongIcon sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            Construction Invoice Template
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
          Create professional construction invoices with CIS deductions and VAT. Download as PDF — free, no sign-up required.
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* ── Company Details ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Your Company Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={company.name}
                onChange={(e) => setCompany((p) => ({ ...p, name: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={company.phone}
                onChange={(e) => setCompany((p) => ({ ...p, phone: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={company.email}
                onChange={(e) => setCompany((p) => ({ ...p, email: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT / Company Registration Number"
                value={company.vatNumber}
                onChange={(e) => setCompany((p) => ({ ...p, vatNumber: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={company.address}
                onChange={(e) => setCompany((p) => ({ ...p, address: e.target.value }))}
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Client Details ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Client / Customer Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Name"
                value={client.name}
                onChange={(e) => setClient((p) => ({ ...p, name: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={client.contact}
                onChange={(e) => setClient((p) => ({ ...p, contact: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Email"
                value={client.email}
                onChange={(e) => setClient((p) => ({ ...p, email: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Address"
                value={client.address}
                onChange={(e) => setClient((p) => ({ ...p, address: e.target.value }))}
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Invoice Details ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Invoice Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={invoiceMeta.number}
                onChange={(e) => setInvoiceMeta((p) => ({ ...p, number: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Invoice Date"
                type="date"
                value={invoiceMeta.date}
                onChange={(e) => setInvoiceMeta((p) => ({ ...p, date: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={invoiceMeta.dueDate}
                onChange={(e) => setInvoiceMeta((p) => ({ ...p, dueDate: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Payment Terms"
                value={invoiceMeta.paymentTerms}
                onChange={(e) => handlePaymentTermsChange(e.target.value)}
                size="small"
              >
                {PAYMENT_TERMS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {invoiceMeta.paymentTerms === 'custom' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Custom Payment Terms"
                  value={invoiceMeta.customTerms}
                  onChange={(e) => setInvoiceMeta((p) => ({ ...p, customTerms: e.target.value }))}
                  size="small"
                  placeholder="e.g. 50% upfront, 50% on completion"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Project / Site Reference"
                value={projectRef}
                onChange={(e) => setProjectRef(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Quote / PO Reference"
                value={poRef}
                onChange={(e) => setPoRef(e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Line Items ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ background: alpha(PRIMARY, 0.06), px: 3, py: 2 }}>
            <SectionTitle>Line Items</SectionTitle>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100 }} align="center">Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }} align="right">Unit Price (£)</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }} align="right">Total (£)</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {computedLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        size="small"
                        placeholder="e.g. Brickwork to rear extension"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        value={line.qty}
                        onChange={(e) => updateLine(line.id, 'qty', e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        select
                        value={line.unit}
                        onChange={(e) => updateLine(line.id, 'unit', e.target.value)}
                        size="small"
                        sx={{ width: 100 }}
                      >
                        {UNITS.map((u) => (
                          <MenuItem key={u} value={u}>{u}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={line.unitPrice}
                        onChange={(e) => updateLine(line.id, 'unitPrice', e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                        sx={{ width: 120 }}
                        placeholder="0.00"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>£{fmt(line.total)}</Typography>
                    </TableCell>
                    <TableCell>
                      {lines.length > 1 && (
                        <IconButton size="small" onClick={() => removeLine(line.id)} sx={{ color: theme.palette.error.main }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 3, py: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={addLine}
              variant="outlined"
              size="small"
              sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Add Line Item
            </Button>
          </Box>
        </Paper>

        {/* ── Totals ── */}
        <Paper elevation={0} sx={{ border: `2px solid ${PRIMARY}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Totals</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cisEnabled}
                      onChange={(e) => setCisEnabled(e.target.checked)}
                      sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                    />
                  }
                  label="Apply CIS Deduction"
                />
                {cisEnabled && (
                  <TextField
                    label="CIS Deduction Rate (%)"
                    value={cisRate}
                    onChange={(e) => setCisRate(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: 200 }}
                  />
                )}
                <TextField
                  label="VAT Rate (%)"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ width: 200 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ background: alpha(PRIMARY, 0.04), borderRadius: 2, p: 2.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Subtotal:</Typography>
                    <Typography fontWeight={600}>£{fmt(subtotal)}</Typography>
                  </Stack>
                  {cisEnabled && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>CIS Deduction ({cisPct}%):</Typography>
                      <Typography fontWeight={600} color="error">-£{fmt(cisAmount)}</Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>VAT ({vatPct}%):</Typography>
                    <Typography fontWeight={600}>£{fmt(vatAmount)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6" fontWeight={700}>Total Due:</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: PRIMARY }}>£{fmt(grandTotal)}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Bank Details ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Bank Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={bank.bankName}
                onChange={(e) => setBank((p) => ({ ...p, bankName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={bank.accountName}
                onChange={(e) => setBank((p) => ({ ...p, accountName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sort Code"
                value={bank.sortCode}
                onChange={(e) => setBank((p) => ({ ...p, sortCode: e.target.value }))}
                size="small"
                placeholder="00-00-00"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={bank.accountNumber}
                onChange={(e) => setBank((p) => ({ ...p, accountNumber: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Notes ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Payment Instructions / Notes</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            placeholder="e.g. Please reference invoice number when making payment. Late payments may incur interest at 8% above base rate per the Late Payment of Commercial Debts Act."
          />
        </Paper>

        {/* ── Download Button ── */}
        <Stack direction="row" spacing={2} mb={5}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => trackDownload(downloadPDF, { defer: true })()}
            variant="contained"
            size="large"
            sx={{ background: PRIMARY, '&:hover': { background: '#006fa0' }, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4 }}
          >
            Download PDF
          </Button>
        </Stack>

        {/* ── Tips Section ── */}
        <Box mb={5}>
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <LightbulbIcon sx={{ color: PRIMARY }} />
            <Typography variant="h5" fontWeight={700}>Tips for Effective Construction Invoicing</Typography>
          </Stack>
          <Grid container spacing={3}>
            {[
              { title: 'Include PO / Quote References', text: 'Always reference the original purchase order or quote number. This speeds up approval and avoids disputes about agreed scope and pricing.' },
              { title: 'Add CIS Deductions Where Applicable', text: 'If you\'re a subcontractor registered under the Construction Industry Scheme, show the CIS deduction clearly so the contractor can verify and report it to HMRC.' },
              { title: 'State Payment Terms Clearly', text: 'Specify when payment is due (e.g. 14 or 30 days) and any late-payment consequences. This protects your rights under the Late Payment of Commercial Debts Act.' },
              { title: 'Include Bank Details on Every Invoice', text: 'Make it easy for clients to pay by including your bank name, sort code, and account number directly on the invoice. Fewer barriers mean faster payment.' },
              { title: 'Invoice Promptly After Completion', text: 'Don\'t wait weeks to raise an invoice. Invoicing while the work is fresh reduces the chance of queries and keeps your cash flow healthy.' },
              { title: 'Keep a Numbered Sequence', text: 'HMRC requires sequential invoice numbering for VAT-registered businesses. Use a consistent format like INV-001, INV-002 to stay compliant and organised.' },
            ].map(({ title, text }) => (
              <Grid item xs={12} md={6} key={title}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, height: '100%' }}>
                  <Typography fontWeight={700} mb={1}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* ── FAQ ── */}
        <Box mb={5}>
          <Typography variant="h5" fontWeight={700} mb={3}>Frequently Asked Questions</Typography>
          <Grid container spacing={3}>
            {[
              {
                q: 'What is CIS and who needs to use it?',
                a: 'The Construction Industry Scheme (CIS) is a UK tax scheme where contractors deduct money from subcontractor payments and pass it to HMRC. If you\'re a subcontractor working for a contractor in construction, CIS deductions (typically 20% or 30%) are applied to the labour portion of your invoice. Your invoices should clearly show the gross amount, CIS deduction, and the net payment due.',
              },
              {
                q: 'How do I handle retention on construction invoices?',
                a: 'Retention (typically 5%) is an amount withheld by the client until defects liability periods expire. Show the retention as a separate line or deduction on your invoice, stating the percentage and the conditions for release. Track retention amounts carefully — they can represent significant sums across multiple projects.',
              },
              {
                q: 'What are the best payment terms for construction?',
                a: 'Common construction payment terms are 14 or 30 days from invoice date. Under the Housing Grants, Construction and Regeneration Act, payment should be made within 17 days of the due date unless otherwise agreed. For larger projects, consider stage payments tied to milestones rather than a single final invoice.',
              },
              {
                q: 'What makes a valid VAT invoice?',
                a: 'A valid UK VAT invoice must include: your business name and address, your VAT registration number, the invoice date and a unique sequential number, the customer\'s name and address, a description of goods or services, the quantity, unit price, VAT rate, total excluding VAT, VAT amount, and the total including VAT.',
              },
            ].map(({ q, a }) => (
              <Grid item xs={12} md={6} key={q}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, height: '100%' }}>
                  <Typography fontWeight={700} mb={1}>{q}</Typography>
                  <Typography variant="body2" color="text.secondary">{a}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        

        {/* ── Footer Links ── */}
        <Divider sx={{ mb: 3 }} />
        <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" mb={4}>
          <Button component={RouterLink} to="/tools/quote-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Quote Template
          </Button>
          <Button component={RouterLink} to="/tools/purchase-order-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Purchase Order Template
          </Button>
          <Button component={RouterLink} to="/" variant="text" sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Back to Struxor Home
          </Button>
        </Stack>
      </Container>
      </PublicPageShell>
    </>
  );
}
